import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    LearningJourney,
    LearningJourneyResponse,
    LearningTopic,
    LearningTopicsResponse,
    TodaySuggestionsResponse,
    SkillProgress,
    SkillsProgressResponse,
    TopicProgress,
    ProgressAnalytics,
    ProgressAnalyticsResponse,
    SuggestionFilters,
    UpdateLearningProgressRequest,
    LearningProgress,
    SuggestedLessonsResponse,
    TopicLesson,
} from '@/services/progressService';
import { TodaySuggestion } from '@/features/student/StudentProgress/types';

// === LEARNING JOURNEY ACTIONS ===
export const fetchLearningJourneyRequest = createAction('progress/fetchLearningJourneyRequest');
export const fetchLearningJourneySuccess = createAction<LearningJourneyResponse>('progress/fetchLearningJourneySuccess');
export const fetchLearningJourneyFailure = createAction<string>('progress/fetchLearningJourneyFailure');

export const updateLearningProgressRequest = createAction<UpdateLearningProgressRequest>('progress/updateLearningProgressRequest');
export const updateLearningProgressSuccess = createAction<LearningProgress>('progress/updateLearningProgressSuccess');
export const updateLearningProgressFailure = createAction<string>('progress/updateLearningProgressFailure');

// === LEARNING TOPICS ACTIONS ===
export const fetchLearningTopicsRequest = createAction('progress/fetchLearningTopicsRequest');
export const fetchLearningTopicsSuccess = createAction<LearningTopicsResponse>('progress/fetchLearningTopicsSuccess');
export const fetchLearningTopicsFailure = createAction<string>('progress/fetchLearningTopicsFailure');

export const fetchTopicProgressRequest = createAction<string>('progress/fetchTopicProgressRequest');
export const fetchTopicProgressSuccess = createAction<TopicProgress>('progress/fetchTopicProgressSuccess');
export const fetchTopicProgressFailure = createAction<string>('progress/fetchTopicProgressFailure');

// === TODAY'S SUGGESTIONS ACTIONS ===
export const fetchTodaySuggestionsRequest = createAction('progress/fetchTodaySuggestionsRequest');
export const fetchTodaySuggestionsSuccess = createAction<TodaySuggestionsResponse>('progress/fetchTodaySuggestionsSuccess');
export const fetchTodaySuggestionsFailure = createAction<string>('progress/fetchTodaySuggestionsFailure');

export const fetchSuggestedLessonsRequest = createAction<SuggestionFilters | undefined>('progress/fetchSuggestedLessonsRequest');
export const fetchSuggestedLessonsSuccess = createAction<SuggestedLessonsResponse>('progress/fetchSuggestedLessonsSuccess');
export const fetchSuggestedLessonsFailure = createAction<string>('progress/fetchSuggestedLessonsFailure');

// === SKILLS PROGRESS ACTIONS ===
export const fetchSkillsProgressRequest = createAction('progress/fetchSkillsProgressRequest');
export const fetchSkillsProgressSuccess = createAction<SkillsProgressResponse>('progress/fetchSkillsProgressSuccess');
export const fetchSkillsProgressFailure = createAction<string>('progress/fetchSkillsProgressFailure');

export const updateSkillProgressRequest = createAction<{ skillType: string; progress: number }>('progress/updateSkillProgressRequest');
export const updateSkillProgressSuccess = createAction<SkillProgress>('progress/updateSkillProgressSuccess');
export const updateSkillProgressFailure = createAction<string>('progress/updateSkillProgressFailure');

// === PROGRESS ANALYTICS ACTIONS ===
export const fetchProgressAnalyticsRequest = createAction<'week' | 'month' | 'year' | undefined>('progress/fetchProgressAnalyticsRequest');
export const fetchProgressAnalyticsSuccess = createAction<ProgressAnalyticsResponse>('progress/fetchProgressAnalyticsSuccess');
export const fetchProgressAnalyticsFailure = createAction<string>('progress/fetchProgressAnalyticsFailure');

// === STATE INTERFACE ===
export interface ProgressState {
    // Learning Journey
    learningJourney: LearningJourney | null;

    // Learning Topics
    learningTopics: LearningTopic[];
    currentTopicProgress: TopicProgress | null;

    // Today's Suggestions
    todaySuggestions: TodaySuggestion[];
    suggestedLessons: TopicLesson[];

    // Skills Progress
    skillsProgress: SkillProgress[];

    // Analytics
    progressAnalytics: ProgressAnalytics | null;

    // Loading States
    isLoadingJourney: boolean;
    isLoadingTopics: boolean;
    isLoadingSuggestions: boolean;
    isLoadingSkills: boolean;
    isLoadingAnalytics: boolean;
    isUpdatingProgress: boolean;

    // Error States
    journeyError: string | null;
    topicsError: string | null;
    suggestionsError: string | null;
    skillsError: string | null;
    analyticsError: string | null;
    updateError: string | null;
}

const initialState: ProgressState = {
    // Learning Journey
    learningJourney: null,

    // Learning Topics
    learningTopics: [],
    currentTopicProgress: null,

    // Today's Suggestions
    todaySuggestions: [],
    suggestedLessons: [],

    // Skills Progress
    skillsProgress: [],

    // Analytics
    progressAnalytics: null,

    // Loading States
    isLoadingJourney: false,
    isLoadingTopics: false,
    isLoadingSuggestions: false,
    isLoadingSkills: false,
    isLoadingAnalytics: false,
    isUpdatingProgress: false,

    // Error States
    journeyError: null,
    topicsError: null,
    suggestionsError: null,
    skillsError: null,
    analyticsError: null,
    updateError: null,
};

const progressSlice = createSlice({
    name: 'progress',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === LEARNING JOURNEY ===
            .addCase(fetchLearningJourneyRequest, (state) => {
                state.isLoadingJourney = true;
                state.journeyError = null;
            })
            .addCase(fetchLearningJourneySuccess, (state, action) => {
                state.isLoadingJourney = false;
                state.learningJourney = action.payload.data;
            })
            .addCase(fetchLearningJourneyFailure, (state, action) => {
                state.isLoadingJourney = false;
                state.journeyError = action.payload;
            })

            // === UPDATE LEARNING PROGRESS ===
            .addCase(updateLearningProgressRequest, (state) => {
                state.isUpdatingProgress = true;
                state.updateError = null;
            })
            .addCase(updateLearningProgressSuccess, (state) => {
                state.isUpdatingProgress = false;
            })
            .addCase(updateLearningProgressFailure, (state, action) => {
                state.isUpdatingProgress = false;
                state.updateError = action.payload;
            })

            // === LEARNING TOPICS ===
            .addCase(fetchLearningTopicsRequest, (state) => {
                state.isLoadingTopics = true;
                state.topicsError = null;
            })
            .addCase(fetchLearningTopicsSuccess, (state, action) => {
                state.isLoadingTopics = false;
                state.learningTopics = action.payload.data;
            })
            .addCase(fetchLearningTopicsFailure, (state, action) => {
                state.isLoadingTopics = false;
                state.topicsError = action.payload;
            })

            // === TOPIC PROGRESS ===
            .addCase(fetchTopicProgressRequest, (state) => {
                state.isLoadingTopics = true;
                state.topicsError = null;
            })
            .addCase(fetchTopicProgressSuccess, (state, action) => {
                state.isLoadingTopics = false;
                state.currentTopicProgress = action.payload;
            })
            .addCase(fetchTopicProgressFailure, (state, action) => {
                state.isLoadingTopics = false;
                state.topicsError = action.payload;
            })

            // === TODAY'S SUGGESTIONS ===
            .addCase(fetchTodaySuggestionsRequest, (state) => {
                state.isLoadingSuggestions = true;
                state.suggestionsError = null;
            })
            .addCase(fetchTodaySuggestionsSuccess, (state, action) => {
                state.isLoadingSuggestions = false;
                state.todaySuggestions = action.payload.data;
            })
            .addCase(fetchTodaySuggestionsFailure, (state, action) => {
                state.isLoadingSuggestions = false;
                state.suggestionsError = action.payload;
            })

            // === SUGGESTED LESSONS ===
            .addCase(fetchSuggestedLessonsRequest, (state) => {
                state.isLoadingSuggestions = true;
                state.suggestionsError = null;
            })
            .addCase(fetchSuggestedLessonsSuccess, (state, action) => {
                state.isLoadingSuggestions = false;
                state.suggestedLessons = action.payload.data;
            })
            .addCase(fetchSuggestedLessonsFailure, (state, action) => {
                state.isLoadingSuggestions = false;
                state.suggestionsError = action.payload;
            })

            // === SKILLS PROGRESS ===
            .addCase(fetchSkillsProgressRequest, (state) => {
                state.isLoadingSkills = true;
                state.skillsError = null;
            })
            .addCase(fetchSkillsProgressSuccess, (state, action) => {
                state.isLoadingSkills = false;
                state.skillsProgress = action.payload.data;
            })
            .addCase(fetchSkillsProgressFailure, (state, action) => {
                state.isLoadingSkills = false;
                state.skillsError = action.payload;
            })

            // === UPDATE SKILL PROGRESS ===
            .addCase(updateSkillProgressRequest, (state) => {
                state.isUpdatingProgress = true;
                state.updateError = null;
            })
            .addCase(updateSkillProgressSuccess, (state, action) => {
                state.isUpdatingProgress = false;
                // Update the specific skill in the array
                const skillIndex = state.skillsProgress.findIndex(
                    skill => skill.skillType === action.payload.skillType
                );
                if (skillIndex !== -1) {
                    state.skillsProgress[skillIndex] = action.payload;
                }
            })
            .addCase(updateSkillProgressFailure, (state, action) => {
                state.isUpdatingProgress = false;
                state.updateError = action.payload;
            })

            // === PROGRESS ANALYTICS ===
            .addCase(fetchProgressAnalyticsRequest, (state) => {
                state.isLoadingAnalytics = true;
                state.analyticsError = null;
            })
            .addCase(fetchProgressAnalyticsSuccess, (state, action) => {
                state.isLoadingAnalytics = false;
                state.progressAnalytics = action.payload.data;
            })
            .addCase(fetchProgressAnalyticsFailure, (state, action) => {
                state.isLoadingAnalytics = false;
                state.analyticsError = action.payload;
            });
    },
});

export default progressSlice.reducer; 